import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ButtonGroup, Message, SelectPicker, Stack, Tag } from 'rsuite';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { cellToBoundary, cellToChildren, getResolution, latLngToCell } from 'h3-js';
import PageContent from '@/components/PageContent';
import { City, SaveCityCellsResult } from '@/models/city.model';
import { fillCityCellsFromGeoJson, getCityCells, getCityCellsInView, saveCityCells } from '@/services/city.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';

type Tool = 'split' | 'remove' | 'add';

const MAX_RES = 11;
const RES_COLORS: Record<number, string> = {
  4: '#311b92', 5: '#4a148c', 6: '#7b1fa2', 7: '#1565c0', 8: '#00897b', 9: '#7cb342', 10: '#f9a825', 11: '#ef6c00',
};
const NEIGHBOUR_COLOR = '#9e9e9e';
const CONFLICT_COLOR = '#d32f2f';

const toLatLngs = (cell: string) => cellToBoundary(cell) as [number, number][];

interface CityCellsProps {
  city?: City;
}

/**
 * H3 cell editor for a city. Cells are the city's border for H3 city finding.
 * Split: replace a cell with its 7 children. Remove: drop a cell. Add: add a cell at the chosen resolution.
 * The server re-compacts on save and refuses cells that overlap another city.
 */
const CityCells: React.FC<CityCellsProps> = ({ city }) => {
  const mapDiv = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map>();
  const cellLayer = useRef<L.LayerGroup>();
  const neighbourLayer = useRef<L.LayerGroup>();

  const [cells, setCells] = useState<Set<string>>(new Set());
  const [savedCells, setSavedCells] = useState<Set<string>>(new Set());
  const [tool, setTool] = useState<Tool>('split');
  const [addRes, setAddRes] = useState(9);
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<SaveCityCellsResult>();

  // Handlers read the latest state through refs (Leaflet listeners are bound once).
  const state = useRef({ cells, tool, addRes });
  state.current = { cells, tool, addRes };
  const cityRef = useRef(city);
  cityRef.current = city;

  const dirty = useMemo(
    () => cells.size !== savedCells.size || Array.from(cells).some(c => !savedCells.has(c)),
    [cells, savedCells],
  );

  const countsByRes = useMemo(() => {
    const counts: Record<number, number> = {};
    cells.forEach(c => { const r = getResolution(c); counts[r] = (counts[r] ?? 0) + 1; });
    return Object.entries(counts).sort(([a], [b]) => Number(a) - Number(b));
  }, [cells]);

  // Map setup
  useEffect(() => {
    if (!mapDiv.current || map.current) return;
    const m = L.map(mapDiv.current, { preferCanvas: true }).setView([city?.lat || 22.3, city?.lon || 114.17], 11);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(m);
    neighbourLayer.current = L.layerGroup().addTo(m);
    cellLayer.current = L.layerGroup().addTo(m);
    m.on('click', e => {
      if (state.current.tool !== 'add') return;
      const cell = latLngToCell(e.latlng.lat, e.latlng.lng, state.current.addRes);
      setCells(prev => new Set(prev).add(cell));
    });
    m.on('moveend', () => loadNeighbours());
    map.current = m;
    return () => { m.remove(); map.current = undefined; };
  }, []);

  useEffect(() => { if (city?.id) loadCells(); }, [city?.id]);

  // Redraw own cells
  useEffect(() => {
    const layer = cellLayer.current;
    if (!layer) return;
    layer.clearLayers();
    cells.forEach(cell => {
      const res = getResolution(cell);
      const color = conflicts.has(cell) ? CONFLICT_COLOR : RES_COLORS[res] ?? '#000';
      L.polygon(toLatLngs(cell), { color, weight: 0.8, fillColor: color, fillOpacity: 0.3 })
        .bindTooltip(`res ${res} · ${cell}`, { sticky: true })
        .on('click', e => { L.DomEvent.stopPropagation(e); onCellClick(cell); })
        .addTo(layer);
    });
  }, [cells, conflicts]);

  const onCellClick = (cell: string) => {
    const { tool: t } = state.current;
    if (t === 'remove') {
      setCells(prev => { const next = new Set(prev); next.delete(cell); return next; });
    } else if (t === 'split') {
      const res = getResolution(cell);
      if (res >= MAX_RES) { toastErrorMessage(`Res ${MAX_RES} is the smallest cell; use Remove.`); return; }
      setCells(prev => {
        const next = new Set(prev);
        next.delete(cell);
        cellToChildren(cell, res + 1).forEach(c => next.add(c));
        return next;
      });
    }
  };

  const fitToCells = (list: string[]) => {
    if (!map.current || list.length === 0) return;
    const bounds = L.latLngBounds(list.flatMap(toLatLngs));
    map.current.fitBounds(bounds);
  };

  const loadCells = async () => {
    if (!city?.id) return;
    setLoading(true);
    try {
      const res = await getCityCells(city.id);
      const list = res.result.cells;
      setCells(new Set(list));
      setSavedCells(new Set(list));
      setConflicts(new Set());
      setResult(undefined);
      fitToCells(list);
    } catch {
      toastErrorMessage('Could not load city cells.');
    } finally {
      setLoading(false);
    }
  };

  const loadNeighbours = async () => {
    const m = map.current;
    const layer = neighbourLayer.current;
    const cityId = cityRef.current?.id;
    if (!m || !layer || !cityId) return;
    if (m.getZoom() < 8) { layer.clearLayers(); return; }
    const b = m.getBounds();
    try {
      const res = await getCityCellsInView({
        minLat: b.getSouth(), minLng: b.getWest(), maxLat: b.getNorth(), maxLng: b.getEast(), excludeCityId: cityId,
      });
      layer.clearLayers();
      res.result.forEach(c =>
        L.polygon(toLatLngs(c.cell), { color: NEIGHBOUR_COLOR, weight: 0.5, fillColor: NEIGHBOUR_COLOR, fillOpacity: 0.25, interactive: false })
          .addTo(layer));
    } catch {
      // neighbours are only context; ignore failures
    }
  };

  const handleSave = async () => {
    if (!city?.id) return;
    setLoading(true);
    try {
      const res = await saveCityCells(city.id, Array.from(cells));
      const r = res.result;
      setResult(r);
      if (r.saved) {
        setCells(new Set(r.cells));
        setSavedCells(new Set(r.cells));
        setConflicts(new Set());
        toastSuccessMessage(`Saved ${r.cells.length} cells.`);
        loadNeighbours();
      } else {
        // Highlight own cells that clash (server reports compacted cells, which may differ from the edited ones)
        const clashing = new Set(r.conflicts.map(c => c.cell));
        setConflicts(new Set(Array.from(cells).filter(c => clashing.has(c))));
      }
    } catch {
      toastErrorMessage('Could not save city cells.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrefillFromGeometry = async () => {
    if (!city?.id || !city.jsonGeometry) return;
    setLoading(true);
    try {
      const res = await fillCityCellsFromGeoJson(city.id, {
        jsonGeometry: city.jsonGeometry, resolution: 10, save: false, skipCellsOfOtherCities: true,
      });
      setCells(new Set(res.result.cells));
      setConflicts(new Set());
      fitToCells(res.result.cells);
      toastSuccessMessage('Filled from the city geometry. Review, then Save.');
    } catch {
      toastErrorMessage('Could not fill from the city geometry.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContent>
      <Stack spacing={10} wrap style={{ marginBottom: 10 }}>
        <ButtonGroup>
          <Button appearance={tool === 'split' ? 'primary' : 'default'} onClick={() => setTool('split')}>Split</Button>
          <Button appearance={tool === 'remove' ? 'primary' : 'default'} onClick={() => setTool('remove')}>Remove</Button>
          <Button appearance={tool === 'add' ? 'primary' : 'default'} onClick={() => setTool('add')}>Add</Button>
        </ButtonGroup>
        {tool === 'add' && (
          <SelectPicker
            cleanable={false}
            searchable={false}
            value={addRes}
            onChange={v => v && setAddRes(v)}
            data={[6, 7, 8, 9, 10, 11].map(r => ({ value: r, label: `Add at res ${r}` }))}
            style={{ width: 150 }}
          />
        )}
        <Button onClick={handlePrefillFromGeometry} disabled={!city?.jsonGeometry || loading}>Fill from city geometry</Button>
        <Button onClick={loadCells} disabled={!dirty || loading}>Discard changes</Button>
        <Button appearance="primary" onClick={handleSave} loading={loading} disabled={!dirty}>Save cells</Button>
      </Stack>

      <Stack spacing={6} wrap style={{ marginBottom: 10 }}>
        <span>{cells.size.toLocaleString()} cells{dirty ? ' (unsaved changes)' : ''}</span>
        {countsByRes.map(([res, count]) => (
          <Tag key={res} style={{ background: RES_COLORS[Number(res)], color: '#fff' }}>res {res}: {count}</Tag>
        ))}
        <Tag style={{ background: NEIGHBOUR_COLOR, color: '#fff' }}>other cities</Tag>
      </Stack>

      {result && !result.saved && (
        <Message type="error" showIcon style={{ marginBottom: 10 }}>
          {result.invalidCells.length > 0 && <div>{result.invalidCells.length} invalid cells.</div>}
          {result.conflicts.length > 0 && (
            <div>
              {result.conflicts.length} cells overlap other cities
              (city ids: {Array.from(new Set(result.conflicts.map(c => c.otherCityId))).join(', ')}).
              They are shown in red; split or remove them and save again.
            </div>
          )}
        </Message>
      )}

      <div style={{ fontSize: 12, color: '#777', marginBottom: 6 }}>
        Split: click a cell to replace it with its 7 smaller children. Remove: click a cell to drop it.
        Add: click the map to add a cell. Neighbouring cities show in grey from zoom 8.
      </div>
      <div ref={mapDiv} style={{ width: '100%', height: 620, borderRadius: 10 }} />
    </PageContent>
  );
};

export default CityCells;
